import TokenService from '@services/internal/token';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import CustomApi from './custom-api';
import { isTokenExpired, isTokenInvalid } from './utils/tokens';

abstract class BaseApi extends CustomApi {
  static isRefreshing = false;
  static refreshSubscribers = [];

  protected constructor(
    baseURL: string,
    config: {
      allowRefreshToken: boolean;
      includeAuthorization: boolean;
    },
  ) {
    super(baseURL);
    this.baseURL = baseURL;

    /**
     * When enabled, add response interceptors for invalid/expired JWT token
     */
    if (config.allowRefreshToken) {
      this.axios.interceptors.response.use(
        (response: AxiosResponse) => response,
        (error: NonNullable<AxiosError['response']>) => {
          if (!error) {
            return Promise.reject(error);
          }

          const { config } = error;
          const originalRequest = config;

          // Invalid token means, that we are going to reset tokens, because they are useless..
          if (isTokenInvalid(error)) {
            BaseApi.isRefreshing = false;
            TokenService.revokeSession();
            return error;
          }

          // If we need to refresh..
          if (isTokenExpired(error)) {
            // Call refresh token, if not already refreshing
            if (!BaseApi.isRefreshing) {
              BaseApi.isRefreshing = true;
              TokenService.refreshAccessToken()
                .then((newSession) => {
                  BaseApi.onRefreshed(newSession.accessToken);
                })
                .catch((err) => {
                  console.log('BaseApi', err);
                  TokenService.revokeSession();
                })
                .finally(() => {
                  BaseApi.isRefreshing = false;
                });
            }

            // Postpone all requests with invalid token in array
            return new Promise((resolve) => {
              // we are passing a function, that accepts token, all wrapped in Promise
              BaseApi.subscribeTokenRefresh((token: string) => {
                // replace the expired token and retry
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(axios(originalRequest));
              });
            });
          }

          // Create verbose error, not just error message
          return Promise.reject(error.data);
        },
      );
    }

    /**
     * Include bearer token in all requests
     */
    if (config.includeAuthorization) {
      this.axios.interceptors.request.use(
        async (config: AxiosRequestConfig) => {
          const session = await TokenService.getSession();

          const newConfig: AxiosRequestConfig = {
            ...config,
            headers: {
              ...config.headers,
              Authorization: `Bearer ${session?.accessToken ?? ''}`,
            },
          };
          return newConfig;
        },
        (error: any) => Promise.reject(error),
      );
    }
  }

  /**
   * Pop all requests in queue and give them new access token
   * @param token new access token
   */
  static onRefreshed(token: string): void {
    // in this case an "fnc" is a function, which we call with new token, its original request
    BaseApi.refreshSubscribers.map((fnc: (token: string) => void) => fnc(token));
    // clear an array after reply
    BaseApi.refreshSubscribers = [];
  }

  /**
   * Function to subscribe to refresh token queue
   * @param callback callback after refresh
   */
  static subscribeTokenRefresh(callback: (token: string) => void): void {
    BaseApi.refreshSubscribers.push(callback as never);
  }
}

export default BaseApi;
